import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { CurrentUser } from "@/lib/types";

export interface ProfileInput {
  first_name?: string;
  last_name?: string;
  phone?: string;
  position?: string;
  bio?: string;
  avatar?: File | null;
}

/** PATCH /api/v1/auth/me/ — current user updates their own profile.
 *  Multipart so the avatar upload field works on the same endpoint.
 *  Invalidates the `me` query so the topbar + sidebar pick up the
 *  new name / avatar without a reload. */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProfileInput) => {
      const form = new FormData();
      if (input.first_name != null) form.append("first_name", input.first_name);
      if (input.last_name  != null) form.append("last_name",  input.last_name);
      if (input.phone      != null) form.append("phone",      input.phone);
      if (input.position   != null) form.append("position",   input.position);
      if (input.bio        != null) form.append("bio",        input.bio);
      if (input.avatar)             form.append("avatar",     input.avatar);
      const { data } = await api.patch<CurrentUser>("/auth/me/", form);
      return data;
    },
    onSuccess: (user) => {
      qc.setQueryData(["me"], user);
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

/** POST /api/v1/auth/change-password/ — body: { current_password, new_password }.
 *  Backend re-checks the current password, runs Django's password
 *  validators on the new one, and rotates the session hash so the
 *  user stays signed in. */
export function useChangePassword() {
  return useMutation({
    mutationFn: async (
      input: { current_password: string; new_password: string },
    ) => {
      const { data } = await api.post<{ detail: string }>(
        "/auth/change-password/", input,
      );
      return data;
    },
  });
}
